import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Project owner's request (2026-09-17): the header's "Search your page…" box is gone, and each
 * screen's title + breadcrumb sits in its place. Not a template file.
 *
 * `HydrogenLayout` wraps header and page in `PageTitleProvider`; the header mounts `PageTitleSlot`;
 * `PageHeader`/`ListHeader` wrap their title in `PageTitle`, which portals into that slot. Outside
 * a provider (the print route, unit tests) `PageTitle` renders in place, so nothing loses its title.
 */
type Slot = [HTMLElement | null, (el: HTMLElement | null) => void];

const SlotContext = createContext<Slot | undefined>(undefined);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const slot = useState<HTMLElement | null>(null);
  return <SlotContext.Provider value={slot}>{children}</SlotContext.Provider>;
}

export function PageTitleSlot({ className }: { className?: string }) {
  const slot = useContext(SlotContext);
  return <div ref={slot?.[1]} className={className} />;
}

export function PageTitle({ children }: { children: ReactNode }) {
  const slot = useContext(SlotContext);
  if (!slot) return <>{children}</>;
  // Inside the layout but before the header's div has mounted: wait one commit instead of
  // flashing the title in the page body.
  return slot[0] ? createPortal(children, slot[0]) : null;
}
